import React, { useEffect, useState, useCallback } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { RefreshCw, UsersRound, Circle } from 'lucide-react';

const ROLE_LABELS = {
  super_admin: 'Super Admin', admin: 'Amministratore', amministratore: 'Amministratore',
  rspp: 'RSPP', consulente_haccp: 'Consulente HACCP', laboratorio_analisi: 'Laboratorio Analisi',
  medico_competente: 'Medico Competente', gerente: 'Gerente UL', collaborator: 'Collaboratore',
  docente: 'Docente', docente_online: 'Docente Online', formatore: 'Formatore',
  azienda: 'Azienda', verificatore: 'Verificatore', tecnico_collaboratore: 'Tecnico Collaboratore',
};

const roleLabel = (r) => ROLE_LABELS[r] || r || '—';

const timeAgo = (iso) => {
  if (!iso) return '—';
  const secs = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (secs < 60) return `${secs} sec fa`;
  const mins = Math.floor(secs / 60);
  return `${mins} min fa`;
};

const fmtTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

export default function OnlineUsersPage() {
  const [users, setUsers] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/admin/online-users?minutes=5');
      setUsers(r.data?.users || []);
      setCount(r.data?.count || 0);
      setLastRefresh(new Date());
    } catch (e) {
      // silenzioso: pagina riservata admin
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 20000); // auto-refresh ogni 20s
    return () => clearInterval(id);
  }, [load]);

  return (
    <div className="space-y-6" data-testid="online-users-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading font-bold text-3xl flex items-center gap-3">
            <UsersRound className="w-8 h-8 text-emerald-600" /> Utenti Online
          </h1>
          <p className="text-muted-foreground mt-1">
            Utenti attivi sulla piattaforma negli ultimi 5 minuti
            {lastRefresh && <span className="text-xs"> · aggiornato alle {fmtTime(lastRefresh.toISOString())}</span>}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="refresh-online-btn">
          <RefreshCw className="w-4 h-4 mr-2" /> Aggiorna
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Circle className="w-3 h-3 fill-emerald-500 text-emerald-500 animate-pulse" />
            <span data-testid="online-count">{count}</span> online adesso
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-10 text-muted-foreground">Caricamento...</div>
          ) : users.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground" data-testid="online-empty">
              Nessun utente attivo negli ultimi 5 minuti.
            </div>
          ) : (
            <div className="divide-y">
              {users.map((u) => (
                <div key={u.id} className="flex items-center justify-between py-3 gap-3" data-testid={`online-user-${u.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <Circle className="w-2.5 h-2.5 fill-emerald-500 text-emerald-500 flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="font-medium text-slate-800 truncate">{u.full_name}</div>
                      <div className="text-xs text-slate-500 truncate">{u.email}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <Badge variant="outline">{roleLabel(u.role)}</Badge>
                    <div className="text-right">
                      <div className="text-sm text-slate-700">{fmtTime(u.last_seen)}</div>
                      <div className="text-xs text-slate-400">{timeAgo(u.last_seen)}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
